const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface RoadmapPhase {
  id: string;
  name: string;
  description: string | null;
  order: number;
  status: 'completed' | 'in-progress' | 'planned';
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  features: RoadmapFeature[];
}

export interface RoadmapFeature {
  id: string;
  title: string;
  description: string;
  icon: string;
  suggestedBy: string | null;
  status: string;
  phaseId: string | null;
  order: number;
  createdAt: string;
  updatedAt: string;
}

// Static fallback data matching the current hardcoded AboutSection content
const FALLBACK_PHASES: RoadmapPhase[] = [
  {
    id: 'fallback-1',
    name: 'Core Worlds',
    description: 'Personal worlds, topic stations, and real-time messaging foundation.',
    order: 1,
    status: 'completed',
    completedAt: '2024-06-01T00:00:00.000Z',
    createdAt: '',
    updatedAt: '',
    features: [],
  },
  {
    id: 'fallback-2',
    name: 'Voice & Video Calls',
    description: 'Direct peer-to-peer voice and video calls with screen sharing.',
    order: 2,
    status: 'completed',
    completedAt: '2024-12-01T00:00:00.000Z',
    createdAt: '',
    updatedAt: '',
    features: [],
  },
  {
    id: 'fallback-3',
    name: 'Polish & Expand',
    description: 'Bug fixes, UI improvements, and new features.',
    order: 3,
    status: 'in-progress',
    completedAt: null,
    createdAt: '',
    updatedAt: '',
    features: [],
  },
];

export async function fetchPhases(): Promise<RoadmapPhase[]> {
  try {
    const res = await fetch(`${API_BASE}/roadmap/phases`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    return FALLBACK_PHASES;
  }
}

export async function fetchUnassignedFeatures(): Promise<RoadmapFeature[]> {
  try {
    const res = await fetch(`${API_BASE}/roadmap/features?unassigned=true`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    return [];
  }
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NewsEntryApi {
  id: string;
  title: string;
  summary: string;
  date: string;
  tags: string[];
  pinned: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteSetting {
  key: string;
  value: string;
  updatedAt: string;
}

export async function fetchFaqs(): Promise<Faq[]> {
  try {
    const res = await fetch(`${API_BASE}/marketing/faqs`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    // Fallback to static data
    const { faqItems } = await import('../data/faq');
    return faqItems.map((item, index) => ({
      id: `fallback-${index}`,
      question: item.question,
      answer: item.answer,
      order: index,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
  }
}

export async function fetchNews(): Promise<NewsEntryApi[]> {
  try {
    const res = await fetch(`${API_BASE}/marketing/news`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    // Fallback to static data
    const { newsEntries } = await import('../data/news');
    return newsEntries.map((entry) => ({
      id: entry.id,
      title: entry.title,
      summary: entry.summary,
      date: entry.date,
      tags: entry.tags,
      pinned: entry.pinned || false,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
  }
}

export async function fetchSettings(): Promise<SiteSetting[]> {
  try {
    const res = await fetch(`${API_BASE}/marketing/settings`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    // Fallback defaults
    return [
      { key: 'launch_date', value: '2026-02-20T00:00:00', updatedAt: new Date().toISOString() },
      { key: 'faq_homepage_limit', value: '5', updatedAt: new Date().toISOString() },
      { key: 'maintenance_mode', value: 'false', updatedAt: new Date().toISOString() },
    ];
  }
}

export async function fetchMyIp(): Promise<string> {
  try {
    const res = await fetch(`${API_BASE}/marketing/my-ip`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.ip || '';
  } catch {
    return '';
  }
}

export async function submitContact(data: { name: string; email: string; message: string }): Promise<void> {
  const res = await fetch(`${API_BASE}/marketing/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export interface SubscriptionCancellationInput {
  fullName: string;
  email: string;
  subscriptionEmail?: string;
  customerReference?: string;
  message?: string;
  confirmCancellation: boolean;
}

export interface SubscriptionCancellationReceipt {
  id: string;
  receivedAt: string;
  message: string;
}

export async function submitSubscriptionCancellation(
  data: SubscriptionCancellationInput,
): Promise<SubscriptionCancellationReceipt> {
  const res = await fetch(`${API_BASE}/marketing/subscription-cancellations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return await res.json();
}

export async function subscribeNewsletter(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/marketing/newsletter/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export interface ChangelogChangeApi {
  id: string;
  type: 'added' | 'changed' | 'fixed' | 'removed';
  description: string;
  order: number;
}

export interface ChangelogEntryApi {
  id: string;
  version: string;
  date: string;
  changes: ChangelogChangeApi[];
  createdAt: string;
  updatedAt: string;
}

export async function fetchChangelog(): Promise<ChangelogEntryApi[]> {
  const res = await fetch(`${API_BASE}/marketing/changelog`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
}
